import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  DatePickerInput,
  TimePickerInput,
  IcPick as icPick,
  IcTarget as icTarget,
} from '@/shared/ui';
import useEventRangePickers from './useEventRangePickers';

export type EventRangePickersProps = {
  onChangePicker: (name: string, time: any, timeString: any) => void;
  startDate: any;
  startTime: any;
  endDate: any;
  endTime: any;
  error: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
  };
};

/**
 * Renders the paired start-time and end-time date and time pickers used by the ECG event modals, each with jump-to and pick-on-viewer actions. Reads refs and handlers from useEventRangePickers, keeps the date and time inputs disabled so values are only set from the ECG viewer, and surfaces per-field messages from the error prop.
 *
 * @param props - Current start and end values, the per-field error map and the change callback.
 * @returns The start-time and end-time picker rows with their icon buttons and tooltips.
 */
const EventRangePickers = ({
  onChangePicker,
  startDate,
  startTime,
  endDate,
  endTime,
  error,
}: EventRangePickersProps) => {
  const {
    jumpStartRef,
    jumpEndRef,
    pickStartRef,
    pickEndRef,
    onChange,
    onClickJumpTo,
    onClickPickOn,
  } = useEventRangePickers({ onChangePicker });

  return (
    <>
      <div className="time-input mb-3">
        <span className="time-input-title">Start time</span>
        <div className="time-input-row">
          <DatePickerInput
            className="time-input-custom__date pr2"
            name="startDate"
            placeholder="MM/DD/YYYY"
            value={startDate}
            onChange={(date, dateString) => onChange('startDate', date, dateString)}
            disabled
            isError={!_.isEmpty(error.startDate)}
            errorMessage={error.startDate}
          />
          <TimePickerInput
            className="time-input-custom__time"
            value={startTime}
            onChange={(time, timeString) => onChange('startTime', time, timeString)}
            disabled
            placeholder="HH:mm:ss"
            format="HH:mm:ss"
            isError={!_.isEmpty(error.startTime)}
            errorMessage={error.startTime}
          />
          <div ref={jumpStartRef}>
            <IconButton
              className="ms-3"
              isFilled
              disabled={!startDate}
              iconComponent={<img src={icTarget} alt="Jump to" />}
              onClick={() => {
                onClickJumpTo(startDate, true);
              }}
            />
          </div>
          <div ref={pickStartRef}>
            <IconButton
              className="ms-3"
              isFilled
              iconComponent={<img src={icPick} alt="Pick on ECG viewer" />}
              onClick={() => {
                onClickPickOn(true);
              }}
            />
          </div>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={jumpStartRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Jump to
          </UncontrolledTooltip>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={pickStartRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Pick on ECG viewer
          </UncontrolledTooltip>
        </div>
      </div>
      <div className="time-input">
        <span className="time-input-title">End time</span>
        <div className="time-input-row">
          <DatePickerInput
            className="time-input-custom__date pr2"
            name="endDate"
            placeholder="MM/DD/YYYY"
            value={endDate}
            onChange={(date, dateString) => onChange('endDate', date, dateString)}
            disabled
            isError={!_.isEmpty(error.endDate)}
            errorMessage={error.endDate}
          />
          <TimePickerInput
            className="time-input-custom__time"
            value={endTime}
            onChange={(time, timeString) => onChange('endTime', time, timeString)}
            disabled
            placeholder="HH:mm:ss"
            format="HH:mm:ss"
            isError={!_.isEmpty(error.endTime)}
            errorMessage={error.endTime}
          />
          <div ref={jumpEndRef}>
            <IconButton
              className="ms-3"
              id="jumpto_end"
              isFilled
              disabled={!endDate}
              iconComponent={<img src={icTarget} alt="Jump to" />}
              onClick={() => {
                onClickJumpTo(endDate, false);
              }}
            />
          </div>
          <div ref={pickEndRef}>
            <IconButton
              className="ms-3"
              id="pickon_end"
              isFilled
              iconComponent={<img src={icPick} alt="Pick on ECG viewer" />}
              onClick={() => {
                onClickPickOn(false);
              }}
            />
          </div>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={jumpEndRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Jump to
          </UncontrolledTooltip>
          <UncontrolledTooltip
            className="custom-tooltip"
            target={pickEndRef}
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            Pick on ECG viewer
          </UncontrolledTooltip>
        </div>
      </div>
    </>
  );
};

export default EventRangePickers;
